import React, { useEffect, useMemo, useState, useCallback, Suspense, lazy } from 'react';
import MapView2D from './MapView2D';
import ErrorBoundary from './ErrorBoundary';
import WeatherPill from './map/WeatherPill';
import DronesPanel from './map/DronesPanel';
import MapControlsPanel from './map/MapControlsPanel';
import TelemetryPill from './map/TelemetryPill';
import { DroneChip, DroneSheet, TelemetryCard, ModeToggle, SearchButton, RoundButton, Sheet } from './mobile/MobileKit';
import { MAP_STYLES, DEFAULT_MAP_STYLE, to3DStyleKey } from '../config/mapStyles';
import { useNotification } from '../contexts/NotificationContext';
import { logger } from '../utils/logger';
import { geocode } from '../utils/geo';

const Map3DRenderer = lazy(() => import('./Map3DRenderer'));

const MAP_MODE_KEY = 'utms_map_2d3d_mode';
const CAMERA_MODE_KEY = 'utms_camera_mode';
const BUILDINGS_KEY = 'utms_3d_buildings';
const MAP_STYLE_KEY = 'utms_map_style_v2';

const DEFAULT_CENTER = { lat: 21.1458, lon: 79.0882 }; // Nagpur

/**
 * MapView — Stitch-design map workspace.
 * Owns the shared map state (projection, style, zones, search) and lays the
 * floating overlay panels over the live 2D (Leaflet) / 3D (Mapbox GL) map.
 */
function MapView(props) {
  const {
    missionMode = false,
    isMobileLayout = false,
    topOffset = 0,
    isActive = true,
    selectedDroneId = null,
    drones = [],
    dronesTelemetry = {},
    sidebarVisible = true,
  } = props;

  const { showToast } = useNotification();

  /* ---------- persisted UI state ---------- */
  const [mapMode, setMapMode] = useState(() => localStorage.getItem(MAP_MODE_KEY) || '2d');
  const [cameraMode, setCameraMode] = useState(() => localStorage.getItem(CAMERA_MODE_KEY) || 'third_person');
  const [show3DBuildings, setShow3DBuildings] = useState(() => localStorage.getItem(BUILDINGS_KEY) !== 'false');
  const [currentStyle, setCurrentStyle] = useState(() => {
    const saved = localStorage.getItem(MAP_STYLE_KEY);
    if (saved && MAP_STYLES[saved]) return saved;
    return MAP_STYLES[DEFAULT_MAP_STYLE] ? DEFAULT_MAP_STYLE : Object.keys(MAP_STYLES)[0];
  });
  const [followDroneId, setFollowDroneId] = useState(null);

  useEffect(() => { localStorage.setItem(MAP_MODE_KEY, mapMode); }, [mapMode]);
  useEffect(() => { localStorage.setItem(CAMERA_MODE_KEY, cameraMode); }, [cameraMode]);
  useEffect(() => { localStorage.setItem(BUILDINGS_KEY, String(show3DBuildings)); }, [show3DBuildings]);
  useEffect(() => { localStorage.setItem(MAP_STYLE_KEY, currentStyle); }, [currentStyle]);

  useEffect(() => {
    if (selectedDroneId && selectedDroneId !== followDroneId) {
      setFollowDroneId(selectedDroneId);
    }
  }, [selectedDroneId, followDroneId]);

  /* ---------- responsive ---------- */
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isCompact = isMobileLayout || viewportWidth <= 768;

  /* ---------- zones (map.geojson) ---------- */
  const [geoJson, setGeoJson] = useState(null);
  const [availableZones, setAvailableZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);

  useEffect(() => {
    const loadZones = async () => {
      try {
        const res = await fetch('/data/map.geojson');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setGeoJson(data);
      } catch (e) {
        logger.error('❌ Error loading zones:', e);
      }
    };
    loadZones();
  }, []);

  useEffect(() => {
    if (geoJson && Array.isArray(geoJson.features)) {
      const zones = new Set();
      geoJson.features.forEach((feature) => {
        const zoneName = feature.properties?.zone || feature.properties?.name;
        if (zoneName) zones.add(zoneName);
      });
      const uniqueZones = Array.from(zones).sort();
      setAvailableZones(uniqueZones);
      setSelectedZones([]); // Default to no zones checked per user request
    }
  }, [geoJson]);

  const handleZoneToggle = useCallback((zone) => {
    setSelectedZones((prev) => (prev.includes(zone) ? prev.filter((z) => z !== zone) : [...prev, zone]));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedZones((prev) => (prev.length === availableZones.length ? [] : [...availableZones]));
  }, [availableZones]);

  /* ---------- location search (mobile search button; 2D map pans to it) ---------- */
  const [searchTarget, setSearchTarget] = useState(null);

  const searchPlace = useCallback(async (q) => {
    try {
      const hit = await geocode(q);
      if (hit) { setSearchTarget(hit); return true; }
      showToast('No location found', 'warning');
    } catch {
      showToast('Search error', 'error');
    }
    return false;
  }, [showToast]);

  /* ---------- mobile sheets ---------- */
  const [droneSheetOpen, setDroneSheetOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);

  /* ---------- live telemetry for the pills ---------- */
  const trackedDrone = useMemo(() => {
    if (selectedDroneId && dronesTelemetry[selectedDroneId]) {
      const d = drones.find((x) => x.id === selectedDroneId);
      return { id: selectedDroneId, name: d?.name || selectedDroneId, type: d?.type || 'N/A', telemetry: dronesTelemetry[selectedDroneId] };
    }
    const firstConnected = Object.entries(dronesTelemetry).find(([, t]) => t?.connected);
    if (firstConnected) {
      const d = drones.find((x) => x.id === firstConnected[0]);
      return { id: firstConnected[0], name: d?.name || firstConnected[0], type: d?.type || 'N/A', telemetry: firstConnected[1] };
    }
    return null;
  }, [selectedDroneId, dronesTelemetry, drones]);

  const trackedPos = useMemo(() => {
    const tel = trackedDrone?.telemetry;
    return tel && tel.lat && tel.lon ? [tel.lat, tel.lon] : null;
  }, [trackedDrone]);
  const weatherCenter = trackedPos ? { lat: trackedPos[0], lon: trackedPos[1] } : DEFAULT_CENTER;

  /* ---------- projection ---------- */
  const canRender3D = !missionMode;
  useEffect(() => {
    if (!canRender3D && mapMode === '3d') setMapMode('2d');
  }, [canRender3D, mapMode]);
  const effectiveMapMode = canRender3D ? mapMode : '2d';

  const showOverlays = !missionMode && !isCompact;

  // Same panel on desktop (right column) and phone (layers sheet).
  const controlsProps = {
    mapMode: effectiveMapMode, setMapMode, canRender3D,
    cameraMode, setCameraMode, show3DBuildings, setShow3DBuildings,
    stylesCatalog: MAP_STYLES, currentStyle, onStyleChange: setCurrentStyle,
    availableZones, selectedZones, onZoneToggle: handleZoneToggle, onSelectAll: handleSelectAll,
  };

  // Where the map's own chrome (zoom stack, recenter) starts on the left: past
  // the floating sidebar. Published as a variable because the 3D map's Mapbox
  // control container is positioned by Mapbox, not by us.
  useEffect(() => {
    const inset = showOverlays ? `${(sidebarVisible ? 260 : 80) + 16}px` : '16px';
    document.documentElement.style.setProperty('--map-left-inset', inset);
    return () => document.documentElement.style.setProperty('--map-left-inset', '16px');
  }, [sidebarVisible, showOverlays]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* ===== map layers ===== */}
      {canRender3D && (
        <div style={{ position: 'absolute', inset: 0, display: effectiveMapMode === '3d' ? 'block' : 'none' }}>
          <ErrorBoundary fallback={<MapView2D {...props} currentStyle={currentStyle} geoJson={geoJson} selectedZones={selectedZones} searchTarget={searchTarget} stitchOverlays={showOverlays} />}>
            <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--bgPrimary)', color: 'var(--accent)', fontWeight: 600, fontFamily: 'var(--font-label)' }}>INITIALIZING 3D ENGINE…</div>}>
              <Map3DRenderer
                location={props.location}
                dronesTelemetry={props.dronesTelemetry}
                activeMissions={props.activeMissions}
                selectedDroneId={props.selectedDroneId}
                drones={props.drones}
                currentUserRole={props.currentUserRole}
                onDroneSelect={props.onDroneSelect}
                styleKey={to3DStyleKey(currentStyle)}
                cameraMode={cameraMode}
                followDroneId={followDroneId}
                onFollowDroneChange={setFollowDroneId}
                show3DBuildings={show3DBuildings}
                isActive={effectiveMapMode === '3d' && isActive}
                topOffset={topOffset}
                isMobileLayout={isMobileLayout}
                sidebarVisible={props.sidebarVisible}
                stitchChrome
              />
            </Suspense>
          </ErrorBoundary>
        </div>
      )}

      {effectiveMapMode === '2d' && (
        <div style={{ position: 'absolute', inset: 0 }}>
          <MapView2D
            {...props}
            currentStyle={currentStyle}
            geoJson={geoJson}
            selectedZones={selectedZones}
            searchTarget={searchTarget}
            locateTarget={trackedPos}
          />
        </div>
      )}

      {/* ===== Stitch overlay chrome (phone) — the Flutter app's map tab ===== */}
      {!missionMode && isCompact && (
        <div className="m-overlay">
          <div className="m-weather">
            <WeatherPill compact lat={weatherCenter.lat} lon={weatherCenter.lon} />
          </div>
          <div className="m-left">
            <DroneChip drone={trackedDrone} telemetry={trackedDrone?.telemetry} onOpen={() => setDroneSheetOpen(true)} />
          </div>
          <div className="m-right">
            {effectiveMapMode === '2d' && <SearchButton onSearch={searchPlace} />}
            <ModeToggle value={effectiveMapMode} onChange={setMapMode} />
            <RoundButton icon="layers" title="Map layers" onClick={() => setLayersOpen(true)} />
          </div>
          <div className="m-bottom-left">
            <TelemetryCard telemetry={trackedDrone?.telemetry} />
          </div>

          {droneSheetOpen && (
            <DroneSheet
              drones={drones}
              dronesTelemetry={dronesTelemetry}
              selectedDroneId={trackedDrone?.id}
              onSelect={(d) => props.onDroneSelect && props.onDroneSelect(d)}
              onClose={() => setDroneSheetOpen(false)}
            />
          )}
          {layersOpen && (
            <Sheet title="MAP LAYERS" onClose={() => setLayersOpen(false)}>
              <MapControlsPanel {...controlsProps} fluid />
            </Sheet>
          )}
        </div>
      )}

      {/* ===== Stitch overlay chrome (desktop) ===== */}
      {showOverlays && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1000, pointerEvents: 'none', padding: 16, display: 'flex' }}>
          {/* left: drones stack — offset past the floating sidebar so the toggle never overlaps it */}
          <div style={{
            position: 'absolute',
            left: (sidebarVisible ? 260 : 80) + 16,
            top: 16, bottom: 16,
            display: viewportWidth < 1100 ? 'none' : 'flex',
            flexDirection: 'column',
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <DronesPanel
              drones={drones}
              dronesTelemetry={dronesTelemetry}
              selectedDroneId={selectedDroneId}
              onDroneSelect={props.onDroneSelect}
            />
          </div>

          {/* top: weather pill, centred in the gap between the drones panel (288, hidden
              below 1100) and the map controls (≤320). No translate: the box IS the gap.
              App.css .map-top-gap drops the labels, then hides the pill, when it is narrow. */}
          <div className="map-top-gap" style={{
            position: 'absolute',
            top: 16,
            left: (sidebarVisible ? 260 : 80) + 16 + (viewportWidth < 1100 ? 0 : 288 + 16),
            right: 16 + 320 + 16,
            display: 'flex',
            justifyContent: 'center',
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <WeatherPill lat={weatherCenter.lat} lon={weatherCenter.lon} />
          </div>

          {/* right: map controls — shrink width on narrow screens */}
          <div style={{
            position: 'absolute',
            right: 16, top: 16, bottom: 16,
            display: 'flex',
            alignItems: 'flex-start',
            maxWidth: viewportWidth < 1100 ? 'min(320px, calc(100vw - 48px))' : 320,
          }}>
            <MapControlsPanel {...controlsProps} />
          </div>

          {/* bottom center: live telemetry pill */}
          <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 48px)' }}>
            <TelemetryPill
              telemetry={trackedDrone?.telemetry || {}}
              connected={!!trackedDrone?.telemetry?.connected}
              droneId={trackedDrone?.id || selectedDroneId}
              droneName={trackedDrone?.name || ''}
              droneType={trackedDrone?.type || ''}
              selected={selectedDroneId !== null || !!trackedDrone}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MapView;
